import React, { useRef, useEffect } from 'react';
import DOMPurify from 'dompurify';
import './RichText.css';

interface Props {
  value: string;
  onChange: (v: string) => void;
  onBlur?: (v: string) => void;
  readOnly?: boolean;
  placeholder?: string;
  minHeight?: number | string;
  /* Hoja blanca con tinta oscura, como un documento. Se usa en la evaluación
     interna para que se reconozca al primer vistazo y se sienta como escribir
     en Word; el resto de los campos sigue en el tema oscuro. */
  papel?: boolean;
}

const RichText: React.FC<Props> = ({
  value,
  onChange,
  onBlur,
  readOnly,
  placeholder = 'Escriba aquí…',
  minHeight = 100,
  papel,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const lastHtml = useRef('');

  // Inicializar contenido al montar. Saneado también en edición: lo guardado
  // puede venir de otro usuario del estudio, y un <img onerror> en una nota se
  // ejecutaría al abrir la ficha.
  useEffect(() => {
    if (ref.current) {
      ref.current.innerHTML = DOMPurify.sanitize(value || '');
      lastHtml.current = value || '';
    }
  }, []); // eslint-disable-line

  // Sincronizar cuando el value cambia externamente (ej: resultado IA)
  // Compara con el DOM real, no con lastHtml.current, para detectar cambios externos correctamente
  useEffect(() => {
    if (!ref.current) return;
    if (ref.current.innerHTML !== (value || '')) {
      ref.current.innerHTML = DOMPurify.sanitize(value || '');
      lastHtml.current = value || '';
    }
  }, [value]);

  const emit = () => {
    const html = ref.current?.innerHTML || '';
    if (html === lastHtml.current) return;
    lastHtml.current = html;
    onChange(html);
  };

  const handleBlur = () => {
    emit();
    onBlur?.(lastHtml.current);
  };

  const exec = (e: React.MouseEvent, cmd: string) => {
    e.preventDefault(); // evita que el editor pierda el foco
    document.execCommand(cmd, false, undefined);
    ref.current?.focus();
    emit();
  };

  if (readOnly) {
    const safeHtml = value
      ? DOMPurify.sanitize(value)
      : `<span class="rte-vacio">${placeholder}</span>`;
    return <div className={papel ? 'rte-view rte-papel' : 'rte-view'} dangerouslySetInnerHTML={{ __html: safeHtml }} />;
  }

  return (
    <div className={papel ? 'rte-editor rte-papel' : 'rte-editor p-input'} style={{ padding: 0, overflow: papel ? 'visible' : 'hidden' }}>
      <div className="rte-toolbar">
        <button type="button" className="rte-btn" title="Negrita (Ctrl+B)" onMouseDown={e => exec(e, 'bold')}>
          <b>B</b>
        </button>
        <button type="button" className="rte-btn" title="Cursiva (Ctrl+I)" onMouseDown={e => exec(e, 'italic')}>
          <i>I</i>
        </button>
        <button type="button" className="rte-btn" title="Subrayado (Ctrl+U)" onMouseDown={e => exec(e, 'underline')}>
          <u>U</u>
        </button>
        <span className="rte-sep" />
        <button type="button" className="rte-btn" title="Lista numerada" onMouseDown={e => exec(e, 'insertOrderedList')}>
          1.
        </button>
        <button type="button" className="rte-btn" title="Lista con viñetas" onMouseDown={e => exec(e, 'insertUnorderedList')}>
          •
        </button>
        <span className="rte-sep" />
        <button type="button" className="rte-btn rte-btn-chica" title="Quitar formato" onMouseDown={e => exec(e, 'removeFormat')}>
          ✕ fmt
        </button>
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        data-placeholder={placeholder}
        onInput={emit}
        onBlur={handleBlur}
        className="rte-area"
        style={{ minHeight }}
      />
    </div>
  );
};

export default RichText;
